import { useEffect, useRef } from "react";
import { useCompanyFilterOptions } from "../../hooks";
import {
  useCompanyFiltersStore,
  useCompanyFiltersStoreApi,
} from "../../provider/CompanyFilterProvider";
import type { UploadedAccountsOptions } from "./options";
import {
  defaultUploadedAccountsSlice,
  type UploadedAccountsSlice,
} from "./slice";

/**
 * Hook that automatically hydrates the uploadedAccounts stats when
 * fileId is present but matchedRows is missing.
 *
 * This handles the case where a segment is loaded with a companyMatch
 * filter - we need to re-fetch the stats from the server.
 */
export function useUploadedAccountsSync(): void {
  const slice = useCompanyFiltersStore(
    (s) => s.slices.uploadedAccounts
  ) as UploadedAccountsSlice;
  const storeApi = useCompanyFiltersStoreApi();
  const options = useCompanyFilterOptions("uploadedAccounts") as
    | UploadedAccountsOptions
    | undefined;

  // Track in-flight request to avoid duplicates
  const requestRef = useRef<string | null>(null);

  useEffect(() => {
    // Only hydrate when we have a fileId but no matchedRows
    // This indicates we loaded from a saved segment
    const needsHydration =
      slice.uploadState === "SUCCESS" &&
      slice.fileId &&
      slice.matchedRows === null;

    if (!needsHydration || !options?.onPoll) return;

    const requestKey = slice.fileId;

    // Skip if we're already fetching this file
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;

    options
      .onPoll(slice.fileId!)
      .then((stats) => {
        // Ignore stale responses
        if (requestRef.current !== requestKey) return;

        // Read the LATEST slice to avoid clobbering concurrent changes
        const latestSlice = storeApi.getState().slices
          .uploadedAccounts as UploadedAccountsSlice;

        // Only update if still in SUCCESS state with same fileId
        if (
          latestSlice.uploadState === "SUCCESS" &&
          latestSlice.fileId === requestKey
        ) {
          storeApi.getState().setSlice("uploadedAccounts", {
            ...latestSlice,
            fileName: stats.fileName ?? latestSlice.fileName,
            matchedRows: stats.matchedRows,
            invalidRows: stats.invalidRows,
            totalRows: stats.totalRows,
            uploadedAt: stats.uploaded,
            matchedAt: stats.lastMatched,
            isPlatformPermissible: stats.isPlatformPermissible ?? null,
            isV1Dataset: stats.isV1Dataset ?? null,
            rgifExclusiveCompany: stats.rgifExclusiveCompany ?? null,
          });
        }
      })
      .catch(() => {
        // If file not found or error, clear the filter
        const latestSlice = storeApi.getState().slices
          .uploadedAccounts as UploadedAccountsSlice;

        if (
          latestSlice.uploadState === "SUCCESS" &&
          latestSlice.fileId === requestKey
        ) {
          storeApi
            .getState()
            .setSlice("uploadedAccounts", defaultUploadedAccountsSlice);
        }
      })
      .finally(() => {
        if (requestRef.current === requestKey) {
          requestRef.current = null;
        }
      });
  }, [slice.uploadState, slice.fileId, slice.matchedRows, options, storeApi]);
}
